import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import {
  DateInput,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  UserIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getListReturnPath, openRecord } from '../../hooks/useNavigateBack';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import {
  ageFromBirthDate,
  formatWage,
  moduleConfig,
} from './config';
import type { CrewListItem } from './types';
import { useCrewList, useCrewLookups } from './useData';

type StatusTab = 'all' | 'ONB' | 'OFB';

type CrewPoolFilters = {
  view: StatusTab;
  search: string;
  rank: string;
  nationality: string;
  availabilityStatus: string;
  manningAgent: string;
  vesselId: string;
  notRecommended: string;
  availableFrom: string;
};

const viewTabs: Array<{
  id: StatusTab;
  label: string;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'all',
    label: 'All',
    emptyTitle: 'No crew files yet',
    emptyHint: 'Create a crew file to manage seafarer records.',
  },
  {
    id: 'ONB',
    label: 'ONB',
    emptyTitle: 'No onboard crew',
    emptyHint: 'Crew currently onboard will show up here.',
  },
  {
    id: 'OFB',
    label: 'OFB',
    emptyTitle: 'No offboard crew',
    emptyHint: 'Crew marked offboard will show up here.',
  },
];

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnPath = getListReturnPath(location);
  const confirm = useConfirm();

  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'crew';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const storageKey = getListStateStorageKey(moduleConfig.module.route.list);
  const [persisted, setPersisted] = usePersistedListFilters<CrewPoolFilters>(storageKey);

  const [view, setView] = useState<StatusTab>(() => persisted?.view ?? 'all');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState(() => persisted?.search ?? '');
  const [search, setSearch] = useState(() => persisted?.search ?? '');
  const [rank, setRank] = useState(() => persisted?.rank ?? '');
  const [nationality, setNationality] = useState(() => persisted?.nationality ?? '');
  const [availabilityStatus, setAvailabilityStatus] = useState(() => persisted?.availabilityStatus ?? '');
  const [manningAgent, setManningAgent] = useState(() => persisted?.manningAgent ?? '');
  const [vesselId, setVesselId] = useState(() => persisted?.vesselId ?? '');
  const [notRecommended, setNotRecommended] = useState(() => persisted?.notRecommended ?? '');
  const [availableFrom, setAvailableFrom] = useState(() => persisted?.availableFrom ?? '');

  useEffect(() => {
    setPersisted({
      view,
      search,
      rank,
      nationality,
      availabilityStatus,
      manningAgent,
      vesselId,
      notRecommended,
      availableFrom,
    });
  }, [
    view,
    search,
    rank,
    nationality,
    availabilityStatus,
    manningAgent,
    vesselId,
    notRecommended,
    availableFrom,
    setPersisted,
  ]);

  const openCrew = (id: number) => {
    openRecord(navigate, moduleConfig.module.route.detail(id), listReturnPath);
  };

  const { lookups } = useCrewLookups();

  const rankOptions = useMemo(
    () =>
      (lookups?.ranks ?? []).map((r) => ({
        value: r.name,
        label: r.abrv ? `${r.abrv} · ${r.name}` : r.name,
      })),
    [lookups?.ranks],
  );
  const nationalityOptions = useMemo(
    () => (lookups?.nationalities ?? []).map((n) => ({ value: n.name, label: n.name })),
    [lookups?.nationalities],
  );
  const availabilityOptions = useMemo(
    () => (lookups?.availabilityStatuses ?? []).map((s) => ({ value: s.name, label: s.name })),
    [lookups?.availabilityStatuses],
  );
  const manningOptions = useMemo(
    () => (lookups?.manningAgents ?? []).map((a) => ({ value: a.name, label: a.name })),
    [lookups?.manningAgents],
  );
  const vesselOptions = useMemo(
    () =>
      (lookups?.vessels ?? []).map((v) => ({
        value: String(v.id),
        label: v.vesselName,
      })),
    [lookups?.vessels],
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    view,
    rank,
    nationality,
    availabilityStatus,
    manningAgent,
    vesselId,
    notRecommended,
    availableFrom,
    itemsPerPage,
  ]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'lastName', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'lastName' ? 'asc' : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const listOptions = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (view !== 'all') filters.transactionStatus = [view];
    if (rank) filters.rank = [rank];
    if (nationality) filters.nationality = [nationality];
    if (availabilityStatus) filters.availabilityStatus = [availabilityStatus];
    if (manningAgent) filters.manningAgent = [manningAgent];
    if (vesselId) filters.vesselId = [vesselId];
    if (notRecommended) filters.notRecommended = [notRecommended];

    const dateRanges = availableFrom
      ? { availableFrom: { start: availableFrom, end: null as string | null } }
      : undefined;

    const hasListFilters = Object.keys(filters).length > 0 || Boolean(dateRanges);
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: hasListFilters
        ? {
            filters,
            ...(dateRanges ? { dateRanges } : {}),
          }
        : undefined,
    };
  }, [
    view,
    rank,
    nationality,
    availabilityStatus,
    manningAgent,
    vesselId,
    notRecommended,
    availableFrom,
    sortConfig,
  ]);

  const { data, isLoading, isError, total, mutate } = useCrewList(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(
    search.trim() ||
      view !== 'all' ||
      rank ||
      nationality ||
      availabilityStatus ||
      manningAgent ||
      vesselId ||
      notRecommended ||
      availableFrom,
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setView('all');
    setRank('');
    setNationality('');
    setAvailabilityStatus('');
    setManningAgent('');
    setVesselId('');
    setNotRecommended('');
    setAvailableFrom('');
  };

  const handleDelete = async (e: React.MouseEvent, item: CrewListItem) => {
    e.stopPropagation();
    const name = [item.lastName, item.firstName].filter((p) => p?.trim()).join(' ') || `Crew #${item.id}`;
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete “${name}” and all related records?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await moduleConfig.delete.handler(item.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || moduleConfig.delete.errorMessage);
    }
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === view) ?? viewTabs[0], [view]);
  const formatDate = moduleConfig.utils.formatDate;

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label }) => ({ id, label }))}
      activeTab={view}
      onTabChange={(value) => setView(value as StatusTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: moduleConfig.search.placeholder,
        title: 'Searches name, ref, phone, and email',
      }}
      actions={
        canEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New
          </Link>
        ) : null
      }
      filters={
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[150px] flex-1 basis-[150px] sm:max-w-[200px]">
            <Select
              value={rank}
              options={rankOptions}
              placeholder="Rank"
              showClearButton
              searchPlaceholder="Search ranks…"
              onChange={(e) => setRank(String(e.target.value))}
            />
          </div>
          <div className="min-w-[140px] flex-1 basis-[140px] sm:max-w-[180px]">
            <Select
              value={nationality}
              options={nationalityOptions}
              placeholder="Nationality"
              showClearButton
              searchPlaceholder="Search…"
              onChange={(e) => setNationality(String(e.target.value))}
            />
          </div>
          <div className="min-w-[140px] flex-1 basis-[140px] sm:max-w-[180px]">
            <Select
              value={availabilityStatus}
              options={availabilityOptions}
              placeholder="Av. status"
              showClearButton
              onChange={(e) => setAvailabilityStatus(String(e.target.value))}
            />
          </div>
          <div className="min-w-[150px] flex-1 basis-[150px] sm:max-w-[200px]">
            <Select
              value={manningAgent}
              options={manningOptions}
              placeholder="Manning agent"
              showClearButton
              searchPlaceholder="Search…"
              onChange={(e) => setManningAgent(String(e.target.value))}
            />
          </div>
          <div className="min-w-[160px] flex-1 basis-[160px] sm:max-w-[220px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => {
                const nextVessel = String(e.target.value);
                setVesselId(nextVessel);
                // Selecting a vessel defaults status to ONB when unset (API also forces ONB).
                if (nextVessel && view === 'all') setView('ONB');
              }}
            />
          </div>
          <div className="min-w-[130px] flex-1 basis-[130px] sm:max-w-[150px]">
            <Select
              value={notRecommended}
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
              placeholder="Not recommended"
              showClearButton
              onChange={(e) => setNotRecommended(String(e.target.value))}
            />
          </div>
          <div className="min-w-[150px] flex-1 basis-[150px] sm:max-w-[180px]">
            <DateInput
              name="availableFrom"
              value={availableFrom}
              onChange={(e) => setAvailableFrom(e.target.value)}
              placeholder="Av. from"
            />
          </div>
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading crew pool…"
      isEmpty={!isError && data.length === 0}
      emptyState={{
        icon: <UserIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching crew' : activeTab.emptyTitle,
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : canEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New crew file
          </Link>
        ) : undefined,
      }}
      banner={
        isError ? (
          <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
            Could not load crew pool.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-[5.5rem] font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="lastName">Name</GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 w-12 font-medium', colors.surface, colors.textMuted)}>Age</th>
            <th className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted)}>Lic Rank</th>
            <th className={cn('sticky top-0 z-10 hidden font-medium md:table-cell', colors.surface, colors.textMuted)}>
              Nationality
            </th>
            <GeneralListSortableTh sortKey="birthDate" className="hidden lg:table-cell">
              Birthdate
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availableFrom" className="hidden xl:table-cell">
              Av. From
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availableTo" className="hidden xl:table-cell">
              Av. to
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden font-medium lg:table-cell', colors.surface, colors.textMuted)}>
              Av. Status
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium md:table-cell', colors.surface, colors.textMuted)}>
              Last Rank
            </th>
            <th className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted)}>Vessel</th>
            <th className={cn('sticky top-0 z-10 w-20 font-medium', colors.surface, colors.textMuted)}>Status</th>
            <th className={cn('sticky top-0 z-10 hidden font-medium lg:table-cell', colors.surface, colors.textMuted)}>
              S/On
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium xl:table-cell', colors.surface, colors.textMuted)}>
              S/Off
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium xl:table-cell', colors.surface, colors.textMuted)}>
              COE
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium xl:table-cell', colors.surface, colors.textMuted)}>
              Wage
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium 2xl:table-cell', colors.surface, colors.textMuted)}>
              Phone
            </th>
            <th className={cn('sticky top-0 z-10 hidden font-medium 2xl:table-cell', colors.surface, colors.textMuted)}>
              Manning
            </th>
            <GeneralListSortableTh sortKey="lastApplicationDate" className="hidden 2xl:table-cell">
              Last Appl
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastContactedDate" className="hidden 2xl:table-cell">
              Last Cntr
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const name = [item.lastName, item.firstName].filter((p) => p?.trim()).join(' ') || '';
            const status = item.currentTransaction?.status;
            const phones = [item.telephone1 || item.mobile1, item.telephone2 || item.mobile2]
              .map((p) => p?.trim())
              .filter(Boolean)
              .join(' / ');
            return (
              <tr
                key={item.id}
                onClick={() => openCrew(item.id)}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <td>
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={moduleConfig.module.route.detail(item.id)}
                      state={{ from: listReturnPath }}
                      title="View"
                      aria-label="View crew file"
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    {canEdit && (
                      <Link
                        onClick={(e) => e.stopPropagation()}
                        to={moduleConfig.module.route.edit(item.id)}
                        state={{ from: listReturnPath }}
                        title="Edit"
                        aria-label="Edit crew file"
                        className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete crew file"
                        onClick={(e) => handleDelete(e, item)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
                        )}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className={cn('whitespace-nowrap font-medium', colors.text)}>{name}</td>
                <td className={cn('whitespace-nowrap tabular-nums', colors.textMuted)}>
                  {ageFromBirthDate(item.birthDate) || ''}
                </td>
                <td className={cn('whitespace-nowrap', colors.textMuted)}>
                  {item.rank?.abrv || item.rank?.name || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {item.nationality?.name || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap lg:table-cell', colors.textMuted)}>
                  {formatDate(item.birthDate ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap xl:table-cell', colors.textMuted)}>
                  {formatDate(item.availableFrom ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap xl:table-cell', colors.textMuted)}>
                  {formatDate(item.availableTo ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap lg:table-cell', colors.textMuted)}>
                  {item.availabilityStatus?.name || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {item.currentTransaction?.rank?.abrv ||
                    item.currentTransaction?.rank?.name || ''}
                </td>
                <td className={cn('whitespace-nowrap', colors.textMuted)}>
                  {item.currentTransaction?.vesselName || ''}
                </td>
                <td className="whitespace-nowrap">
                  {status ? (
                    <Badge
                      variant={status === 'ONB' ? 'success' : 'default'}
                      className="shrink-0 rounded-md border px-2 py-0.5"
                    >
                      {status}
                    </Badge>
                  ) : (
                    <span className={colors.textMuted}></span>
                  )}
                </td>
                <td className={cn('hidden whitespace-nowrap lg:table-cell', colors.textMuted)}>
                  {formatDate(item.currentTransaction?.signOnDate ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap xl:table-cell', colors.textMuted)}>
                  {formatDate(item.currentTransaction?.signOffDate ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap xl:table-cell', colors.textMuted)}>
                  {formatDate(item.currentTransaction?.coeDate ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap xl:table-cell', colors.textMuted)}>
                  {formatWage(item) || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap 2xl:table-cell', colors.textMuted)}>
                  {phones || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap 2xl:table-cell', colors.textMuted)}>
                  {item.manningAgent?.name || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap 2xl:table-cell', colors.textMuted)}>
                  {formatDate(item.lastApplicationDate ?? undefined)}
                </td>
                <td className={cn('hidden whitespace-nowrap 2xl:table-cell', colors.textMuted)}>
                  {formatDate(item.lastContactedDate ?? undefined)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
